import React, { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Flex, Icon, Image, Text } from "@chakra-ui/react";
import SocialAccountPropType from "../../../_types//SocialAccount";
import PostPropType from "../../../_types/Post";
import { formatPostMessage } from "../../../_utils/postMessageFormatter";
import SocialAccountAvatar from "../SocialAccountAvatar/SocialAccountAvatar";
import { getFixedPhotosVideosOrder } from "../PostEditor/helpers";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const SocialPostCardInstagram = ({
  account,
  post,
  time,
  ...props
}) => {
  const listRef = useRef(null);
  const [carouselActiveIndex, setCarouselActiveIndex] = useState(0);
  const type = account?.social_type;

  const stopVideos = useCallback(() => {
    listRef.current?.querySelectorAll("video")?.forEach((item) => {
      item.pause();
      item.currentTime = 0;
    });
  }, [listRef]);

  const handleCarouselPrevClick = useCallback(() => {
    stopVideos();
    setCarouselActiveIndex(carouselActiveIndex - 1);
  }, [carouselActiveIndex, setCarouselActiveIndex, stopVideos]);

  const handleCarouselNextClick = useCallback(() => {
    stopVideos();
    setCarouselActiveIndex(carouselActiveIndex + 1);
  }, [carouselActiveIndex, setCarouselActiveIndex, stopVideos]);

  if (!account || !post || !type) {
    return null;
  }

  const renderHeader = () => (
    <Box>
      <Flex sx={{ alignItems: "center", padding: "14px 16px" }}>
        <Box
          sx={{
            position: "relative",
            width: "32px",
            height: "32px",
            lineHeight: "32px",
          }}
        >
          <SocialAccountAvatar
            sx={{
              position: "relative",
              width: "full",
              height: "full",
              fontSize: "16px",
              borderRadius: "full",
              background: "#fafafa",
              _after: {
                position: "absolute",
                inset: "0",
                borderRadius: "full",
                border: "1px solid rgba(0, 0, 0, .0975)",
                content: '""',
              },
            }}
            name={account.name}
            src={account.image}
            socialType={account.social_type}
          />
        </Box>
        <Box sx={{ flex: "1 1 0%", margin: "0 14px" }}>
          <Box
            sx={{
              padding: "2px",
              fontSize: "14px",
              fontWeight: "600",
              lineHeight: "18px",
              color: "#262626",
            }}
          >
            <Box>{account.nickname}</Box>
          </Box>
        </Box>
        {/* <Box sx={{ marginLeft: "auto" }}>
          <Box>
            <Box
              sx={{ fontSize: "14px", color: "#262626" }}
              _before={{ content: '"\\2022\\2022\\2022"' }}
            />
          </Box>
        </Box> */}
      </Flex>
    </Box>
  );

  const renderFooter = () => {
    const comments = (post?.comments || []).map((item, index) => ({
      ...item,
      id: index,
    }));

    const actionStyles = {
      alignItems: "center",
      justifyContent: "center",
    };

    const actionIconStyles = {
      padding: "8px",
      color: "#262626",
      boxSizing: "content-box",
      width: "24px",
      height: "24px",
      fill: "currentColor",
    };

    return (
      <Box>
        <Box>
          <Flex
            sx={{
              alignItems: "center",
              margin: "4px -8px 0",
              padding: "0 16px",
            }}
          >
            <Flex sx={actionStyles}>
              <Icon sx={actionIconStyles} viewBox="0 0 48 48">
                <path
                  // eslint-disable-next-line max-len
                  d="M34.6 6.1c5.7 0 10.4 5.2 10.4 11.5 0 6.8-5.9 11-11.5 16S25 41.3 24 41.9c-1.1-.7-4.7-4-9.5-8.3-5.7-5-11.5-9.2-11.5-16C3 11.3 7.7 6.1 13.4 6.1c4.2 0 6.5 2 8.1 4.3 1.9 2.6 2.2 3.9 2.5 3.9.3 0 .6-1.3 2.5-3.9 1.6-2.3 3.9-4.3 8.1-4.3m0-3c-4.5 0-7.9 1.8-10.6 5.6-2.7-3.7-6.1-5.5-10.6-5.5C6 3.1 0 9.6 0 17.6c0 7.3 5.4 12 10.6 16.5.6.5 1.3 1.1 1.9 1.7l2.3 2c4.4 3.9 6.6 5.9 7.6 6.5.5.3 1.1.5 1.6.5.6 0 1.1-.2 1.6-.5 1-.6 2.8-2.2 7.8-6.8l2-1.8c.7-.6 1.3-1.2 2-1.7C42.7 29.6 48 25 48 17.6c0-8-6-14.5-13.4-14.5z"
                  fill="currentColor"
                />
              </Icon>
            </Flex>
            <Flex sx={actionStyles}>
              <Icon sx={actionIconStyles} viewBox="0 0 48 48">
                <path
                  // eslint-disable-next-line max-len
                  d="M47.5 46.1l-2.8-11c1.8-3.3 2.8-7.1 2.8-11.1C47.5 11 37 .5 24 .5S.5 11 .5 24 11 47.5 24 47.5c4 0 7.8-1 11.1-2.8l11 2.8c.8.2 1.6-.6 1.4-1.4zm-3-22.1c0 4-1 7-2.6 10-.2.4-.3.9-.2 1.4l2.1 8.4-8.3-2.1c-.5-.1-1-.1-1.4.2-1.8 1-5.2 2.6-10 2.6-11.4 0-20.6-9.2-20.6-20.5S12.7 3.5 24 3.5 44.5 12.7 44.5 24z"
                  clipRule="evenodd"
                  fillRule="evenodd"
                  fill="currentColor"
                />
              </Icon>
            </Flex>
            <Flex sx={actionStyles}>
              <Icon sx={actionIconStyles} viewBox="0 0 48 48">
                <path
                  // eslint-disable-next-line max-len
                  d="M47.8 3.8c-.3-.5-.8-.8-1.3-.8h-45C.9 3.1.3 3.5.1 4S0 5.2.4 5.7l15.9 15.6 5.5 22.6c.1.6.6 1 1.2 1.1h.2c.5 0 1-.3 1.3-.7l23.2-39c.4-.4.4-1 .1-1.5zM5.2 6.1h35.5L18 18.7 5.2 6.1zm18.7 33.6l-4.4-18.4L42.4 8.6 23.9 39.7z"
                  fill="currentColor"
                />
              </Icon>
            </Flex>
            <Flex sx={{ ...actionStyles, marginLeft: "auto" }}>
              <Icon sx={actionIconStyles} viewBox="0 0 48 48">
                <path
                  // eslint-disable-next-line max-len
                  d="M43.5 48c-.4 0-.8-.2-1.1-.4L24 29 5.6 47.6c-.4.4-1.1.6-1.6.3-.6-.2-1-.8-1-1.4v-45C3 .7 3.7 0 4.5 0h39c.8 0 1.5.7 1.5 1.5v45c0 .6-.4 1.2-.9 1.4-.2.1-.4.1-.6.1zM24 26c.8 0 1.6.3 2.2.9l15.8 16V3H6v39.9l15.8-16c.6-.6 1.4-.9 2.2-.9z"
                  fill="currentColor"
                />
              </Icon>
            </Flex>
          </Flex>
          <Box sx={{ padding: "0 16px" }}>
            {post.message ? (
              <Box
                sx={{
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "18px",
                  color: "#262626",
                  marginBottom: "5px",
                }}
              >
                <Text
                  sx={{
                    display: "inline",
                    fontWeight: "600",
                  }}
                >
                  {`${account.nickname} `}
                </Text>
                <Text
                  sx={{
                    display: "inline",
                    "& a": {
                      color: "#00376b",
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: formatPostMessage(post.message),
                  }}
                />
              </Box>
            ) : null}
            {comments.map((item) => (
              <Box
                key={item.id}
                sx={{
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "18px",
                  color: "#262626",
                  marginBottom: "5px",
                }}
              >
                <Text
                  sx={{
                    display: "inline",
                    fontWeight: "600",
                  }}
                >
                  {`${account.nickname} `}
                </Text>
                <Text
                  sx={{
                    display: "inline",
                    "& a": {
                      color: "#00376b",
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: formatPostMessage(item.message),
                  }}
                />
              </Box>
            ))}
            <Box sx={{ marginBottom: "4px" }}>
              <Box
                sx={{
                  fontSize: "10px",
                  letterSpacing: "0.2px",
                  fontWeight: "400",
                  lineHeight: "18px",
                  textTransform: "uppercase",
                  color: "#8e8e8e",
                }}
              >
                {time}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderMedia = () => {
    const mediaItems = getFixedPhotosVideosOrder(
      post?.photos,
      post?.videos
    ).items;
    const firstMediaItemMeta = mediaItems?.[0]?.data?.meta;
    const ratio =
      firstMediaItemMeta?.width && firstMediaItemMeta?.height
        ? firstMediaItemMeta?.width / firstMediaItemMeta?.height
        : 1;
    const carouselFirstItemActive = carouselActiveIndex === 0;
    const carouselLastItemActive =
      carouselActiveIndex === mediaItems.length - 1;

    const carouselNavStyles = {
      position: "absolute",
      top: "50%",
      zIndex: "1",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "26px",
      height: "26px",
      transform: "translateY(-50%)",
      backgroundColor: "#ffffff",
      borderRadius: "50%",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      color: "rgba(0, 0, 0, 0.4)",
      fontSize: "14px",
      cursor: "pointer",
    };

    const mediaItemStyles = {
      position: "absolute",
      inset: "0",
      display: "block",
      width: "full",
      height: "full",
    };

    return (
      <Box>
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-around",
              width: `${100 * mediaItems.length}%`,
              transform: `translateX(${
                carouselActiveIndex * (100 / mediaItems.length) * -1
              }%)`,
              transition: "transform 200ms ease-in-out",
            }}
            ref={listRef}
          >
            {mediaItems.map((item) => (
              <Box key={item.data.url} flex="1">
                <Box
                  position="relative"
                  overflow="hidden"
                  paddingBottom={`${(1 / ratio) * 100}%`}
                >
                  {item.field === "videos" ? (
                    <Box
                      sx={{
                        ...mediaItemStyles,
                        display: "flex",
                        alignItems: "center",
                        background: "black",
                      }}
                    >
                      <Box
                        as="video"
                        width="100%"
                        height="auto"
                        src={item.data.url}
                        controls
                        preload="metadata"
                      />
                    </Box>
                  ) : (
                    <Image
                      sx={{
                        ...mediaItemStyles,
                        objectFit: "cover",
                      }}
                      src={item.data.url}
                    />
                  )}
                </Box>
              </Box>
            ))}
          </Box>
          {mediaItems.length > 1 && (
            <Box
              position="absolute"
              bottom="15px"
              left="6px"
              right="6px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              pointerEvents="none"
            >
              {mediaItems.map((item, index) => (
                <Box
                  key={item.data.url}
                  width="6px"
                  height="6px"
                  marginX="4px"
                  borderRadius="full"
                  background="white"
                  transition="opacity .2s ease-in-out"
                  opacity={carouselActiveIndex === index ? "1" : "0.4"}
                ></Box>
              ))}
            </Box>
          )}
          <Box
            sx={{
              ...carouselNavStyles,
              left: "8px",
              opacity: carouselFirstItemActive ? "0" : "1",
              visibility: carouselFirstItemActive ? "hidden" : null,
            }}
            onClick={carouselFirstItemActive ? null : handleCarouselPrevClick}
          >
            <Icon as={FaChevronLeft} />
          </Box>
          <Box
            sx={{
              ...carouselNavStyles,
              right: "8px",
              opacity: carouselLastItemActive ? "0" : "1",
              visibility: carouselLastItemActive ? "hidden" : null,
            }}
            onClick={carouselLastItemActive ? null : handleCarouselNextClick}
          >
            <Icon as={FaChevronRight} />
          </Box>
        </Box>
      </Box>
    );
  };

  const showMedia = !!post?.videos?.length || !!post?.photos?.length;

  return (
    <Box
      sx={{
        background: "white",
        fontFamily:
          '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
        border: "1px solid #dbdbdb",
        borderRadius: "3px",
        maxWidth: "616px",
      }}
      {...props}
    >
      <Box>
        {renderHeader()}
        <Box>
          <Box>{showMedia ? renderMedia() : null}</Box>
        </Box>
        {renderFooter()}
      </Box>
    </Box>
  );
};

SocialPostCardInstagram.propTypes = {
  account: SocialAccountPropType.isRequired,
  post: PostPropType.isRequired,
  time: PropTypes.string.isRequired,
};

export default SocialPostCardInstagram;
